import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';

const CTASection = () => {
  return (
    <section className="py-28 bg-hero-bg relative overflow-hidden">

      {/* Aurora orbs */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        {/* Base radial glow */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_60%_at_50%_100%,rgba(99,102,241,0.22),transparent)]" />

        {/* Orbs */}
        <div className="aurora-1 absolute top-[-30%] left-[15%] w-[250px] h-[250px] sm:w-[400px] sm:h-[400px] lg:w-[500px] lg:h-[500px] rounded-full bg-indigo-600/30 blur-[100px]" />
        <div className="aurora-2 absolute bottom-[-20%] right-[10%] w-[200px] h-[200px] sm:w-[350px] sm:h-[350px] lg:w-[450px] lg:h-[450px] rounded-full bg-violet-600/25 blur-[110px]" />
        <div className="aurora-3 absolute top-[40%] left-[45%] w-[150px] h-[150px] sm:w-[200px] sm:h-[200px] lg:w-[300px] lg:h-[300px] rounded-full bg-purple-700/20 blur-[90px]" />

        {/* Dot grid overlay */}
        <div
          className="absolute inset-0 opacity-[0.055]"
          style={{
            backgroundImage: 'radial-gradient(circle, rgba(139,92,246,0.9) 1px, transparent 1px)',
            backgroundSize: '32px 32px',
          }}
        />

        {/* Subtle line grid */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-2xl mx-auto text-center"
        >
          {/* Eyebrow */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase px-4 py-2 rounded-full mb-8 border border-indigo-500/40 bg-indigo-500/10 text-indigo-300">
              <Sparkles className="w-3 h-3" />
              Kom igång idag
            </span>
          </motion.div>

          {/* Headline */}
          <h2 className="text-4xl sm:text-[52px] font-extrabold tracking-[-0.025em] leading-[1.08] mb-6 gradient-text">
            Redo att ta nästa steg?
          </h2>

          {/* Sub */}
          <p className="text-lg text-white/50 mb-10 leading-[1.75] font-light">
            Beskriv vad som tar onödig tid. Vi återkommer med ett konkret upplägg — ingen pitch, bara lösning.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/contact">
              <button className="group btn-glow relative overflow-hidden bg-gradient-to-r from-indigo-500 via-violet-500 to-purple-600 text-white font-semibold px-8 py-3.5 rounded-xl text-sm flex items-center gap-2 shadow-lg shadow-indigo-500/30">
                Skicka förfrågan
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
                <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </button>
            </Link>
            <Link to="/ai-consulting">
              <button className="border border-white/15 hover:border-white/35 text-white/70 hover:text-white font-semibold px-8 py-3.5 rounded-xl text-sm transition-all duration-200 hover:bg-white/5">
                Se våra tjänster
              </button>
            </Link>
          </div>

          {/* Trust badges */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.35, duration: 0.6 }}
            className="mt-12 flex items-center justify-center gap-6 flex-wrap"
          >
            {['Aifinity AB', 'AI · Automation · SaaS', 'B2B · Sverige'].map((item, i) => (
              <span key={item} className="flex items-center gap-6">
                <span className="text-white/30 text-sm">{item}</span>
                {i < 2 && <span className="w-px h-4 bg-white/10" />}
              </span>
            ))}
          </motion.div>

        </motion.div>
      </div>
    </section>
  );
};

export default CTASection;
